import React from "react";
import PeopleWidget from "components/channels/widgets/PeopleWidget";
import ImagesWidget from "components/channels/widgets/ImagesWidget";
import BannerWidget from "components/channels/widgets/BannerWidget";
import TextWidget from "components/channels/widgets/TextWidget";
import ChannelRssWidget from "components/channels/widgets/RssWidget";
import BlogWidget from "components/channels/widgets/BlogWidget";
import AppointmentsWidget from "components/channels/widgets/AppointmentsWidget";
import SpotlightWidget from "components/channels/widgets/SpotlightWidget";
import LinkWidget from "components/channels/widgets/LinkWidget";
import NavigationWidget from "components/channels/widgets/NavigationWidget";

type PeopleWidgetProps = Parameters<typeof PeopleWidget>[0];
type TextWidgetProps = Parameters<typeof TextWidget>[0];
type ImagesWidgetProps = Parameters<typeof ImagesWidget>[0];
type ChannelRsssWidgetProps = Parameters<typeof ChannelRssWidget>[0];
type BlogWidgetProps = Parameters<typeof BlogWidget>[0];
type AppointmentsWidgetProps = Parameters<typeof AppointmentsWidget>[0];
type SpotlightWidgetProps = Parameters<typeof SpotlightWidget>[0];
type BannerWidgetProps = Parameters<typeof BannerWidget>[0];
type LinkWidgetProps = Parameters<typeof LinkWidget>[0];
type NavigationWidgetProps = Parameters<typeof NavigationWidget>[0];

type WidgetProps =
  | ({ type: "people" } & PeopleWidgetProps)
  | ({ type: "text" } & TextWidgetProps)
  | ({ type: "images" } & ImagesWidgetProps)
  | ({ type: "banner" } & BannerWidgetProps)
  | ({ type: "rss" } & ChannelRsssWidgetProps)
  | ({ type: "blog" } & BlogWidgetProps)
  | ({ type: "appointments" } & AppointmentsWidgetProps)
  | ({ type: "spotlight" } & SpotlightWidgetProps)
  | ({ type: "link" } & LinkWidgetProps)
  | ({ type: "navigation" } & NavigationWidgetProps)
  | { type: string };

function translateBlogArticles(blogProps: BlogWidgetProps): BlogWidgetProps {
  if (!blogProps.latest_articles || blogProps.latest_articles.length === 0) {
    return blogProps;
  }

  const translatedArticles = [...blogProps.latest_articles];

  translatedArticles.forEach((article, index) => {
    const translationKey = `article_${article.id}`;
    if (blogProps[translationKey]) {
      translatedArticles[index] = {
        ...article,
        title: blogProps[translationKey],
      };
    }
  });

  return {
    ...blogProps,
    latest_articles: translatedArticles,
  };
}

function translateAppointment(
  appointmentsWidgetProps: AppointmentsWidgetProps & {
    appointment_name?: string;
    appointment_description_rendered?: string;
  },
): AppointmentsWidgetProps {
  if (!appointmentsWidgetProps.appointment) {
    return appointmentsWidgetProps;
  }

  let updatedAppointment = { ...appointmentsWidgetProps.appointment };

  if (appointmentsWidgetProps.appointment_name) {
    updatedAppointment = {
      ...updatedAppointment,
      name: appointmentsWidgetProps.appointment_name,
    };
  }

  if (appointmentsWidgetProps.appointment_description_rendered) {
    updatedAppointment = {
      ...updatedAppointment,
      description_rendered:
        appointmentsWidgetProps.appointment_description_rendered,
    };
  }

  return {
    ...appointmentsWidgetProps,
    appointment: updatedAppointment,
  };
}

function translateSpotlightArticles(
  spotlightProps: SpotlightWidgetProps,
): SpotlightWidgetProps {
  if (!spotlightProps.articles || spotlightProps.articles.length === 0) {
    return spotlightProps;
  }

  const translatedArticles = [...spotlightProps.articles];

  translatedArticles.forEach((article, index) => {
    const translationKey = `article_${article.id}`;
    if (spotlightProps[translationKey]) {
      translatedArticles[index] = {
        ...article,
        title: spotlightProps[translationKey],
      };
    }
  });

  return {
    ...spotlightProps,
    articles: translatedArticles,
  };
}

export default function WidgetByType({ type, ...props }: WidgetProps) {
  switch (type) {
    case "people": {
      const peopleProps = props as PeopleWidgetProps;
      return <PeopleWidget {...peopleProps} />;
    }
    case "text": {
      const textProps = props as TextWidgetProps;
      return <TextWidget {...textProps} />;
    }
    case "images": {
      const imagesProps = props as ImagesWidgetProps;
      return <ImagesWidget {...imagesProps} />;
    }
    case "banner": {
      const bannerProps = props as BannerWidgetProps;
      return <BannerWidget {...bannerProps} />;
    }
    case "rss": {
      const rssProps = props as ChannelRsssWidgetProps;
      return <ChannelRssWidget {...rssProps} />;
    }
    case "blog": {
      const blogProps = props as BlogWidgetProps;
      const translatedBlogProps = translateBlogArticles(blogProps);
      return <BlogWidget {...translatedBlogProps} />;
    }
    case "appointments": {
      const appointmentsWidgetProps = props as AppointmentsWidgetProps;
      const translatedAppointmentsProps = translateAppointment(
        appointmentsWidgetProps,
      );
      return <AppointmentsWidget {...translatedAppointmentsProps} />;
    }
    case "spotlight": {
      const spotlightProps = props as SpotlightWidgetProps;
      const translatedSpotlightProps =
        translateSpotlightArticles(spotlightProps);
      return <SpotlightWidget {...translatedSpotlightProps} />;
    }
    case "link": {
      const linkProps = props as LinkWidgetProps;
      return <LinkWidget {...linkProps} />;
    }
    case "navigation": {
      const navigationProps = props as NavigationWidgetProps;
      return <NavigationWidget {...navigationProps} />;
    }
    default:
      return window.Settings?.development ? (
        <div className={"alert alert-info prose"}>
          TODO Implement case for <code>{type}</code> in WidgetByTypes
        </div>
      ) : null;
  }
}
